import { money } from '../../../utils/region';
import { PR_FILE_MAX_BYTES, PR_RUN_STATUSES, PR_RUN_TYPES, PR_TRI_MARKERS, PrAccount, PrSchema } from '../../../utils/api/payroll';
import { dmy } from '../ui';

// ── The payroll screens' shared words (batch 10 R4) ──────────────────────────────────────────────────
// What Payroll › Employees (PayrollEmployees.tsx) and its import window (PayrollImport.tsx) both say: a
// field's name, the sentence for each refusal the routes give, a stored date or month day-first, the door a
// change came through, and a logged value as a person reads it. Moved here unchanged from
// PayrollEmployees.tsx (R3) when the import window needed them too.
// ⛔ Here, not exported from PayrollEmployees.tsx: that file renders the import window, so the window
// importing from it would be an import cycle. Every function takes `t` from its caller's useTranslation —
// nothing here calls i18next itself, and no string is translated at module scope.

export type TF = (key: string, opts?: Record<string, unknown>) => string;
export const fieldLabel = (t: TF, f: string) => t(`prl_f_${f}`);
export const fieldsList = (t: TF, fs: any): string => (Array.isArray(fs) ? fs : []).map((f: string) => fieldLabel(t, f)).join(', ');
/** The route's answer turned into a sentence: every refusal the server can give has its own words. */
export const prErr = (e: any, t: TF): string => {
  const p = e?.payload || {};
  switch (p.code) {
    case 'tier_c': return t('prl_err_tier_c', { fields: fieldsList(t, p.fields) });
    case 'tier_c_worse': return t('prl_err_tier_c_worse', { fields: fieldsList(t, p.fields) });
    case 'invalid': return t('prl_err_invalid', { fields: fieldsList(t, p.fields) });
    case 'stale': return t('prl_err_stale');
    case 'code_taken': return t('prl_err_code_taken');
    case 'in_payroll': return t('prl_err_in_payroll');
    case 'pay_forbidden': return t('prl_err_pay_forbidden');
    case 'has_hire_date': return t('prl_err_has_hire_date');
    case 'no_sso_date': return t('prl_err_no_sso_date');
    case 'file_type': return t('prl_err_file_type');
    case 'too_big': return t('prl_err_file_too_big', { mb: PR_FILE_MAX_BYTES / (1024 * 1024) });
    // The import's commit (R4): the two the window cannot know beforehand — someone changed a matched employee
    // since the preview (the lines it differs on), or the token is gone (30 minutes, used, or a restart) — and
    // the tick it never sends without (a backstop).
    case 'import_stale': return t('prl_err_import_stale', { lines: (Array.isArray(p.lines) ? p.lines : []).join(', ') });
    case 'import_expired': return t('prl_err_import_expired');
    case 'import_name_matches': return t('prl_err_import_name_matches');
    default: return String(e?.message || '') || t('prl_err_generic');
  }
};
// A start date may be a whole date or only a month (sso_status holds 'YYYY-MM'): UK order either way —
// dd/mm/yyyy or mm/yyyy — never the stored ISO (the History tab's rule, historyValue below).
export const monthOrDay = (iso: string) => (/^\d{4}-\d{2}$/.test(iso) ? `${iso.slice(5)}/${iso.slice(0, 4)}` : dmy(iso));
/** The door a change came through (employee_change_log.source), as a short word — never the raw id, which
 *  carries a portal request's or an import's UUID. A door not named here is shown as recorded. */
export const SOURCES: [string, string][] = [
  ['route:payroll.', 'prl_src_payroll'], ['route:clients.', 'prl_src_clients'], ['route:pnd1.', 'prl_src_tax'],
  ['portal:', 'prl_src_portal'], ['tg:', 'prl_src_telegram'], ['import:', 'prl_src_import'],
];
export const sourceLabel = (t: TF, source: string): string => { const hit = SOURCES.find(([p]) => String(source || '').startsWith(p)); return hit ? t(hit[1]) : String(source || ''); };
/** A logged value as a person reads it (the History tab, the import preview's changes): dates day-first, a
 *  choice in the reader's language, a tick as Yes / No, the expense account by its code, a file as what it
 *  is — never a raw id. Anything else (names, codes, amounts) is data and shown as recorded.
 *  (R5) A three-way field's "answered" marker (welfare_answered, PR_TRI_MARKERS) is logged beside it but is
 *  no field of the schema: it reads Yes / No like a tick, never 0 / 1. */
export function historyValue(t: TF, schema: PrSchema, accounts: PrAccount[], fieldKey: string, v: any): string {
  const s = v === null || v === undefined ? '' : String(v);
  const meta = schema.fields.find(f => f.key === fieldKey);
  const options = fieldKey === 'separation_kind' ? schema.separationKinds : meta?.kind === 'select' ? (meta.options || []) : null;
  if (options && options.includes(s)) return t(`prl_opt_${fieldKey}_${s || 'blank'}`);   // '' is a choice of its own where a list has it
  if (s === '') return t('prl_blank');
  if (meta?.kind === 'bool' || fieldKey === 'deleted' || Object.values(PR_TRI_MARKERS).includes(fieldKey)) return Number(s) ? t('prl_yes') : t('prl_no');
  if (meta?.kind === 'date' || meta?.kind === 'month_or_date' || fieldKey === 'termination_date') {
    return /^\d{4}-\d{2}(-\d{2})?$/.test(s) ? monthOrDay(s) : s;
  }
  if (meta?.kind === 'account') { const a = accounts.find(x => x.id === s); return a ? a.code : t('prl_hist_account_other'); }
  if (fieldKey === 'photo_doc_id') return t('prl_hist_photo');
  if (fieldKey === 'id_doc_id') return t('prl_hist_file');
  if (fieldKey === 'pnd1_allowances') {
    try {
      const list = JSON.parse(s);
      if (Array.isArray(list)) return list.length ? list.map((x: any) => `${String(x?.name || '')} ${money(Number(x?.amount) || 0)}`).join(' · ') : t('prl_blank');
    } catch { /* shown as recorded */ }
  }
  return s;
}

// ── A payroll run's words (batch 10 R6, brief §4.3) — shared with Payroll › Payroll List (PayrollRuns.tsx) ──────
// A run's state and type as the reader's language says them, and where a year-to-date month's figures come from.
// The lists are the server's (api/payroll.ts PR_RUN_STATUSES / PR_RUN_TYPES, mirrors held equal by
// scripts/testPayrollI18n.ts); a value outside them — a state a newer server added — is shown as sent, never as a
// raw prl_ key.
/** A run's state (draft · calculated · approved · posted · reversed · void). */
export const runStatusLabel = (t: TF, s: string): string => ((PR_RUN_STATUSES as readonly string[]).includes(s) ? t(`prl_run_status_${s}`) : String(s || ''));
/** A run's type (monthly · daily · off-cycle). */
export const runTypeLabel = (t: TF, s: string): string => ((PR_RUN_TYPES as readonly string[]).includes(s) ? t(`prl_run_type_${s}`) : String(s || ''));
/** Where a month of the tax year is read from (services/payrollCalc.ts YTD_SOURCES): the opening, that month's
 *  ภ.ง.ด.1 line (or the figure captured when it was filed), a gap (employed, and nothing on record), or nothing
 *  (not employed then). `form` is the form's name, written by the caller as a literal — never translated. */
export function ytdSourceLabel(t: TF, source: string, monthText: string, form: string): string {
  switch (source) {
    case 'opening': return t('prl_ytd_src_opening');
    case 'pnd1_line': return t('prl_ytd_src_pnd1_line', { form, month: monthText });
    case 'pnd1_filed': return t('prl_ytd_src_pnd1_filed', { form, month: monthText });
    case 'gap': return t('prl_ytd_src_gap');
    case 'none': return t('prl_ytd_src_none');
    default: return String(source || '');
  }
}
/** A month of a year as the screens write it: mm/yyyy. */
export const mmYyyy = (year: number, month: number): string => `${String(month).padStart(2, '0')}/${year}`;
